import type { Environment } from 'types/environment'
import type { GTMEventName } from '../types/gtm'

interface SplitTest {
  active: boolean
  id: string
  name: string // Dev responsible for choosing a fitting name
  variants: string[]
  weights: number[]
  defaultVariant: string // If not in trafficPercentage, this is the default variant
  gtmEventNames: GTMEventName[] // GTM event names to track, pageview and purchase are always tracked

  includedMarkets?: string[] // Select the markets to be included, can be found here: configuration/storefronts.configuration.ts
  excludedMarkets?: string[]
  startDate?: string
  endDate?: string
  trafficPercentage: number // Percentage of traffic that should be included in the split test, 0-100.
  disableISRForPages?: string[] // Glob patterns, example: '/storefrontsGlob/*(happyblog|about-us|faq|shipping|happynews)/**'
  disableISRForStorefronts?: string[]
}

export const splitTests: SplitTest[] = [
  {
    name: 'Middleware Cookie Logic',
    id: 'MIDDLEWARE_COOKIE_LOGIC',
    variants: ['old_logic', 'new_logic'],
    weights: [50, 50],
    defaultVariant: 'new_logic',
    active: false, // Enable after A/B Test is ready
    trafficPercentage: 100,
    gtmEventNames: ['general', 'productClick', 'addToCart', 'checkout'],
  },
  {
    active: true,
    name: 'Carousel',
    id: 'LP_03_TM_ALL',
    gtmEventNames: ['general', 'pageBlockClick', 'addToCart', 'purchase'],
    variants: ['variant1', 'variant2'],
    defaultVariant: 'variant1',
    weights: [50, 50],
    trafficPercentage: 100,
    includedMarkets: ['uk', 'nl', 'be', 'fr', 'gl'],
  },
  // {
  //   name: 'Posthog TEST',
  //   id: 'LP_02_ALL_ALL',
  //   variants: ['variant1', 'variant2'],
  //   weights: [50, 50],
  //   defaultVariant: 'variant2',
  //   active: true,
  //   trafficPercentage: 40,
  //   audience: 'uk',
  //   gtmEventNames: ['addToCart'],
  // },

]

const HALF_A_YEAR_IN_MILLISECONDS = 1000 * 60 * 60 * 24 * 180
export const splitTestCookieOptions = {
  path: '/',
  maxAge: HALF_A_YEAR_IN_MILLISECONDS,
  secure: true,
  sameSite: 'strict' as const,
}

export function getSplitTestCookieOptions(test: SplitTest) {
  const maxAge: number = test.endDate ? new Date(test.endDate).getTime() - new Date().getTime() : splitTestCookieOptions.maxAge

  return {
    ...splitTestCookieOptions,
    maxAge,
  }
}

export const getSplitTestCookieString = (testId: string) => `experiment-${testId}`

export function getActiveSplitTests(currentMarketCountryCode: Environment.CountryCode): SplitTest[] {
  return splitTests.filter((test) => {
    if (!test.active)
      return false

    const currentDate = new Date().toISOString()
    if (test.startDate && test.startDate > currentDate)
      return false

    if (test.endDate && test.endDate < currentDate)
      return false

    if (test.includedMarkets) {
      const lowercasedAudience = test.includedMarkets.map(market => market.toLowerCase())
      if (!lowercasedAudience.includes(currentMarketCountryCode.toLowerCase())) {
        return false
      }
    }

    if (test.excludedMarkets) {
      const lowercasedAudience = test.excludedMarkets.map(market => market.toLowerCase())
      if (lowercasedAudience.includes(currentMarketCountryCode.toLowerCase())) {
        return false
      }
    }

    return true
  })
}

export function getRandomVariant(test: SplitTest) {
  const randomValue = Math.random() * 100
  let sum = 0

  for (let i = 0; i < test.weights.length; i++) {
    sum += test.weights[i]

    if (randomValue < sum)
      return test.variants[i]
  }

  return test.defaultVariant
}

export function getVariant(test: SplitTest) {
  const isIncludedInTest = test.trafficPercentage ? Math.random() * 100 <= test.trafficPercentage : false
  return isIncludedInTest ? getRandomVariant(test) : undefined
}

export function getAllISRDisabledPages(): string[] {
  const pages = new Set<string>()
  for (const test of splitTests) {
    if (test.disableISRForPages) {
      for (const page of test.disableISRForPages)
        pages.add(page)
    }
  }

  return Array.from(pages)
}

export function getAllISRDisabledStorefronts(): string[] {
  const storefronts = new Set<string>()
  for (const test of splitTests) {
    if (test.disableISRForStorefronts) {
      for (const storefront of test.disableISRForStorefronts)
        storefronts.add(storefront)
    }
  }

  return Array.from(storefronts)
}
